import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import { Dialog, DialogContent, DialogActions, DialogTitle } from '@material-ui/core';
import { trackPromise } from 'react-promise-tracker';
import { toastError } from '../../utils/Functions';
import { getFile, getFileAndDownload } from './FileAPI';
import { getCancelToken, getSource } from '../../utils/AccountsAPI';

var loadImage = require('blueimp-load-image');

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
}));

const distance = (x1, y1, x2, y2) => Math.pow(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2), 0.5);

export default function FilePreviewDialog(props) {
  const classes = useStyles();

  useEffect(() => {
    var origDistance = 0;
    var scale = 1;
    var x = 0;
    var y = 0;
    var prevScale = 0;
    var origX = 0;
    var origY = 0;
    var prevX = 0;
    var prevY = 0;

    var source = getSource();

    const start = (img: HTMLElement) => (e) => {
      img.style.transitionDuration = '0s';
      if (e.touches.length === 1) {
        prevX = x;
        prevY = y;
        origX = e.touches[0].clientX;
        origY = e.touches[0].clientY;
      } else if (e.touches.length >= 2) {
        if (scale !== 1) prevScale = scale - 1;
        origX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        origY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        origDistance = distance(e.touches[0].clientX, e.touches[0].clientY, e.touches[1].clientX, e.touches[1].clientY);
      }
    };

    const zoom = (img: HTMLElement) => (e) => {
      let x1 = e.touches[0]?.clientX;
      let x2 = e.touches[1]?.clientX;
      let y1 = e.touches[0]?.clientY;
      let y2 = e.touches[1]?.clientY;

      if (e.touches.length > 1) {
        scale = prevScale + distance(x1, y1, x2, y2) / origDistance;
        x = prevX + ((x1 + x2) / 2 - origX);
        y = prevY + ((y1 + y2) / 2 - origY);
        img.style.transform = `translate(${x}px,${y}px) scale(${scale})`;
      } else {
        x = prevX + (x1 - origX);
        y = prevY + (y1 - origY);
        img.style.transform = `translate(${x}px,${y}px) scale(${scale})`;
      }
    };

    const end = (img: HTMLElement) => (e) => {
      if (e.touches.length === 1) {
        prevX = x;
        prevY = y;
        origX = e.touches[0].clientX;
        origY = e.touches[0].clientY;
      } else if (e.touches.length === 0) {
        origDistance = 0;
        var currScale = scale;
        var currX = origX;
        var currY = origY;
        setTimeout(() => {
          if (origDistance === 0 && scale === currScale && currX === origX && currY === origY) {
            prevScale = 0;
            prevX = 0;
            prevY = 0;
            x = 0;
            y = 0;
            scale = 1;
            img.style.transitionDuration = '.5s';
            img.style.transform = 'scale(' + scale + ')';
          }
        }, 2000);
      }
    };

    const handleLoad = (img, data) => {
      if (img.type === 'error') {
        toastError(props.toast, 'Error loading image: ' + props.file.fileNickname);
      } else {
        var imgElement = document.getElementById(props.file.id + '_imageLocation');
        if (imgElement) {
          var child = imgElement?.appendChild(img);
          child.id = props.file.id + '_image';
          imgElement.style.zIndex = '2';
          imgElement?.addEventListener('touchstart', start(child));
          imgElement?.addEventListener('touchmove', zoom(child));
          imgElement?.addEventListener('touchend', end(child));
          imgElement?.addEventListener('touchcancel', end(child));
        }
      }
    };

    if (props.file && props.open) {
      trackPromise(
        getFile(props.file.uniqueId, () => { }, getCancelToken(source)).then((response) => {
          let elem = document.getElementById(props.file.id + '_imageLocation');
          var loadImageOptions: any = {
            canvas: true,
            orientation: 1,
            contain: true,
            maxWidth: elem ? parseFloat(window.getComputedStyle(elem).getPropertyValue('width').replace('px', '')) - 48 : undefined,
            maxHeight: elem ? parseFloat(window.getComputedStyle(elem).getPropertyValue('height').replace('px', '')) - 16 : undefined,
            pixelRatio: window.devicePixelRatio,
            imageSmoothingQuality: 'high',
          };
          loadImage.parseMetaData(response.data, (data) => {
            loadImage(response.data, handleLoad, loadImageOptions);
          });
          return Promise.resolve();
        })
      ).catch((err) => {
        toastError(props.toast, err.message);
      });
    }

    return () => {
      source.cancel();
    };
  }, [props.file, props.toast, props.open]);

  const downloadFile = () => {
    if (props.file && props.open) {
      trackPromise(getFileAndDownload(props.file.uniqueId, props.file.fileNickname + props.file.fileExtension)).catch((err) => {
        toastError(props.toast, err);
      });
    }
  };

  return (
    <>
      <Dialog className={classes.root} open={props.open} fullScreen={true}>
        <DialogTitle style={{ backgroundColor: '#1976D2', color: 'white' }}>{props.file?.fileNickname + props.file?.fileExtension || ''}</DialogTitle>
        <DialogContent style={{ textAlign: 'center' }} id={props.file?.id + '_imageLocation'}></DialogContent>
        <DialogActions>
          <Button onClick={downloadFile} color='primary'>
            Download
          </Button>
          <Button onClick={() => props?.close()} color='primary'>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
